/**
 * @fileoverview Shared report data-scope selector for tenant and system template builders.
 * @module ui-admin/template-builder/ReportDataScopeControl
 *
 * Responsibilities: Present scope choices and apply the shared section invariant.
 * Dependencies: React and documentSectionEditors/types.
 * Architecture: Pure controlled presentation; no persistence, API, or telemetry.
 * Notes: Complex visit enables the canonical summary and participant sections.
 */
import type { DocumentConfig } from './documentSectionEditors/types';
import { applyReportDataScopeToDocumentConfig } from './documentSectionEditors/types';

/**
 * @contract ReportDataScopeControlProps
 * Purpose: Controlled inputs for selecting a template's report data scope.
 * Fields: config is the immutable builder model; onChange receives the updated
 *   model; disabled prevents edits while preserving the displayed value.
 * Rules: onChange is invoked only with configs produced by the shared pure scope helper.
 * Sync: No persistence; host pages persist through their existing local-first services.
 */
export interface ReportDataScopeControlProps {
  config: DocumentConfig;
  onChange: (config: DocumentConfig) => void;
  disabled?: boolean;
}

/**
 * @component ReportDataScopeControl
 * Purpose: Present Single case and Complex visit scope choices.
 * Props: ReportDataScopeControlProps.
 * Behavior: Selecting Complex visit enables canonical summary/participant rows;
 *   selecting Single case disables them while preserving one row each.
 * Data Source: Controlled DocumentConfig supplied by the host builder.
 * Constraints: Presentation-only; no API, Dexie, persistence, or telemetry calls.
 * Performance: O(n) section normalization per selection; no subscriptions or effects.
 */
export function ReportDataScopeControl({ config, onChange, disabled = false }: ReportDataScopeControlProps) {
  return (
    <div className="space-y-1">
      <label htmlFor="report-data-scope" className="block text-sm font-medium text-gray-700">
        Report data scope
      </label>
      <select
        id="report-data-scope"
        aria-describedby="report-data-scope-help"
        value={config.reportDataScope ?? 'singleCase'}
        onChange={(event) =>
          onChange(
            applyReportDataScopeToDocumentConfig(
              config,
              event.target.value as 'singleCase' | 'complexVisit',
            ),
          )
        }
        disabled={disabled}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
      >
        <option value="singleCase">Single case</option>
        <option value="complexVisit">Complex visit</option>
      </select>
      <p id="report-data-scope-help" className="text-xs text-gray-500">
        Single case renders one inspection case. Complex visit renders the visit summary and its governed participants.
      </p>
    </div>
  );
}
